import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Mail, Lock, Zap, Star, Globe2, CheckCircle2, ArrowLeft, Loader2, RefreshCw, ScanLine, ArrowRight,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const TRUST_TOP = [
  [ShieldCheck, "Secure Access", "Your data is 100% safe and encrypted", "text-violet-400"],
  [Mail, "Instant Verification", "Quick OTP verification for seamless access", "text-techblue-light"],
  [Lock, "Trusted Platform", "Enterprise grade security you can rely on", "text-violet-300"],
];

const TRUST_STRIP = [
  [ShieldCheck, "Bank Level Security", "256-bit SSL Encryption"],
  [Zap, "Quick Verification", "OTP in less than 30 sec"],
  [Lock, "Privacy First", "Your privacy is our priority"],
  [Star, "Trusted by Businesses", "Secure platform for growth"],
];

const TRUST_BOTTOM = [
  [ShieldCheck, "100% SECURE PLATFORM", "Your security is our top priority."],
  [Globe2, "Global Standards", "Compliant with international security standards."],
  [Star, "Built for Growth", "Designed for businesses that scale globally."],
];

function PhoneFrame({ step, title, accent, children, testid }) {
  return (
    <div className="relative" data-testid={testid}>
      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-techblue text-white font-mono text-sm font-bold flex items-center justify-center z-10">{step}</span>
      <p className="text-center text-xs font-mono uppercase tracking-widest text-slate-300 mb-4 pt-3">{title}</p>
      <div className={`w-56 mx-auto bg-navy-950 border border-slate-600 rounded-[2rem] overflow-hidden shadow-2xl shadow-black/60 border-t-4 ${accent}`}>
        <div className="flex items-center justify-between px-4 pt-2.5 text-[8px] font-mono text-slate-500">
          <span>9:41</span><span className="w-10 h-1 bg-slate-700 rounded-full" />
        </div>
        <div className="p-4 min-h-[240px]">{children}</div>
      </div>
    </div>
  );
}

function OtpBoxes({ value, onChange, color, testid }) {
  const refs = useRef([]);
  const digits = value.split("");
  const setDigit = (i, d) => {
    const clean = d.replace(/\D/g, "").slice(-1);
    const next = (value + "      ").slice(0, 6).split("");
    next[i] = clean;
    onChange(next.join("").trimEnd());
    if (clean && i < 5) refs.current[i + 1]?.focus();
  };
  return (
    <div className="flex justify-center gap-2" data-testid={testid}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input key={i} ref={(el) => (refs.current[i] = el)} inputMode="numeric" maxLength={1}
          value={digits[i] || ""} onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => { if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus(); }}
          data-testid={`${testid}-digit-${i}`}
          className={`w-9 h-11 text-center font-mono text-lg font-semibold bg-navy-800 border border-slate-700 rounded-sm text-slate-100 focus:outline-none ${color}`} />
      ))}
    </div>
  );
}

export default function SecureRegister() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    email: params.get("email") || "",
    company: params.get("company") || "",
    phone: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [emailOtp, setEmailOtp] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [seconds, setSeconds] = useState(28);

  useEffect(() => {
    if (step < 2) return;
    setSeconds(28);
    const t = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [step]);

  const init = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/secure-register/init", form);
      setSession(data);
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (channel, code) => {
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/secure-register/verify", { email: form.email, channel, code });
      if (channel === "email") setStep(3);
      else {
        await api.post("/auth/secure-register/finish", { email: form.email });
        if (inviteToken) {
          try { await api.post(`/invite/${inviteToken}/request`); } catch (e) { /* already processed */ }
        }
        toast.success("Account created & verified. Welcome to 10XGLOBAL!");
        window.location.href = "/dashboard";
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="secure-register-page">
      {/* HEADER */}
      <section className="relative overflow-hidden border-b border-slate-800" data-testid="secure-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(124,58,237,0.14),transparent_55%)]" />
        <div className="relative max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-20 pb-14 text-center">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
            <span className="text-violet-400">Secure.</span> <span className="text-techblue-light">Verify.</span> <span className="text-slate-100">Connect.</span>
          </h1>
          <p className="mt-4 font-mono text-[11px] md:text-sm tracking-[0.2em] text-slate-400">· SAFE LOGIN · OTP VERIFICATION · 100% SECURE ·</p>
          <div className="grid grid-cols-3 gap-4 md:gap-10 mt-10 max-w-2xl mx-auto">
            {TRUST_TOP.map(([Icon, t, d, cls]) => (
              <div key={t} className="text-center">
                <Icon size={26} className={`${cls} mx-auto mb-2.5`} strokeWidth={1.5} />
                <p className="text-xs font-semibold text-slate-200">{t}</p>
                <p className="text-[10px] text-slate-500 mt-1 leading-snug">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHONE MOCK FLOW */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-16 grid md:grid-cols-3 gap-10" data-testid="phone-flow">
        <PhoneFrame step="1" title="LOGIN TO YOUR ACCOUNT" accent="border-t-techblue" testid="mock-login">
          <p className="font-display font-black text-sm"><span className="text-gold">10X</span><span className="text-slate-100">GLOBAL</span></p>
          <p className="font-display font-bold text-slate-100 text-sm mt-3">Welcome Back!</p>
          <p className="text-[9px] text-slate-500">Login to your account</p>
          <div className="flex gap-3 mt-3 text-[9px] font-mono"><span className="text-techblue-light border-b border-techblue-light pb-0.5">Email</span><span className="text-slate-500">Mobile</span></div>
          <div className="mt-2.5 space-y-2">
            <div className="bg-navy-800 border border-slate-700 rounded-sm px-2.5 py-2 text-[9px] text-slate-500 flex items-center gap-1.5"><Mail size={10} /> Enter your email</div>
            <div className="bg-navy-800 border border-slate-700 rounded-sm px-2.5 py-2 text-[9px] text-slate-500 flex items-center gap-1.5"><Lock size={10} /> Enter your password</div>
          </div>
          <div className="mt-3 bg-gradient-to-r from-techblue to-violet-500 text-white text-[10px] font-bold text-center py-2 rounded-sm">Login</div>
        </PhoneFrame>

        <PhoneFrame step="2" title="VERIFY EMAIL" accent="border-t-violet-500" testid="mock-email">
          <div className="w-12 h-12 rounded-full border-2 border-violet-400 bg-violet-500/15 flex items-center justify-center mx-auto mt-2"><Mail size={20} className="text-violet-300" /></div>
          <p className="text-[9px] text-slate-400 text-center mt-3">We've sent a 6-digit OTP to</p>
          <p className="text-[10px] font-mono text-slate-200 text-center mt-0.5">you@company.com</p>
          <div className="flex justify-center gap-1.5 mt-3">{[2, 4, 8, 6, 7, 1].map((d) => <span key={d} className="w-6 h-7 bg-navy-800 border border-slate-700 rounded-sm text-[10px] font-mono text-slate-200 flex items-center justify-center">{d}</span>)}</div>
          <div className="mt-3 bg-gradient-to-r from-violet-500 to-techblue text-white text-[10px] font-bold text-center py-2 rounded-sm">Verify OTP</div>
          <p className="text-[8px] text-slate-500 text-center mt-2.5 flex items-center justify-center gap-1"><ShieldCheck size={9} className="text-emerald-400" /> 100% Secure</p>
        </PhoneFrame>

        <PhoneFrame step="3" title="VERIFY MOBILE" accent="border-t-emerald-500" testid="mock-mobile">
          <div className="w-12 h-12 rounded-full border-2 border-emerald-400 bg-emerald-500/15 flex items-center justify-center mx-auto mt-2"><Lock size={20} className="text-emerald-300" /></div>
          <p className="text-[9px] text-slate-400 text-center mt-3">We've sent a 6-digit OTP to</p>
          <p className="text-[10px] font-mono text-slate-200 text-center mt-0.5">+91 9XXXX XX210</p>
          <div className="flex justify-center gap-1.5 mt-3">{[3, 7, 1, 9, 6, 2].map((d) => <span key={d} className="w-6 h-7 bg-navy-800 border border-slate-700 rounded-sm text-[10px] font-mono text-slate-200 flex items-center justify-center">{d}</span>)}</div>
          <div className="mt-3 bg-emerald-500 text-navy-950 text-[10px] font-bold text-center py-2 rounded-sm">Verify OTP</div>
          <p className="text-[8px] text-slate-500 text-center mt-2.5 flex items-center justify-center gap-1"><ShieldCheck size={9} className="text-emerald-400" /> 100% Secure</p>
        </PhoneFrame>
      </section>

      {/* REAL WIZARD */}
      <section className="border-y border-slate-800 bg-navy-900/40" data-testid="secure-wizard-section">
        <div className="max-w-xl mx-auto px-4 py-16">
          <h2 className="font-display text-2xl font-bold text-slate-100 text-center">START YOUR SECURE REGISTRATION</h2>
          <div className="flex justify-center gap-2 mt-5 mb-8">
            {["DETAILS", "EMAIL OTP", "MOBILE OTP"].map((l, i) => (
              <span key={l} className={`text-[10px] font-mono uppercase tracking-widest border rounded-sm px-3 py-1.5 ${step > i ? "border-gold text-gold bg-gold/10" : "border-slate-700 text-slate-500"}`}>{i + 1}. {l}</span>
            ))}
          </div>

          {step === 1 && (
            <form onSubmit={init} className="card-surface hairline-gold p-7 space-y-4" data-testid="secure-form">
              {[["name", "Full Name", "Your full name", "text"], ["email", "Work Email", "you@company.com", "email"], ["phone", "Mobile Number", "+91 98XXX XXXXX", "tel"], ["company", "Company Name", "Your company name", "text"], ["password", "Password", "Min. 8 characters", "password"]].map(([k, label, ph, type]) => (
                <div key={k}>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
                  <input type={type} required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    placeholder={ph} data-testid={`secure-${k}-input`} className="input-dark" />
                </div>
              ))}
              {error && <p className="text-sm text-red-400" data-testid="secure-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="secure-send-otp-btn" className="btn-gold w-full disabled:opacity-60">
                {loading ? <><Loader2 size={15} className="animate-spin" /> SENDING OTP…</> : "SEND VERIFICATION OTPs"}
              </button>
              <p className="text-center text-xs text-slate-500">Already a member? <Link to="/login" className="text-gold font-semibold">Login</Link></p>
            </form>
          )}

          {step >= 2 && (
            <div className="card-surface hairline-gold p-7" data-testid="otp-step">
              <button onClick={() => { setStep(1); setError(""); }} className="text-xs text-slate-500 hover:text-gold flex items-center gap-1 mb-5"><ArrowLeft size={12} /> Edit details</button>
              {step === 2 ? (
                <>
                  <p className="text-sm text-slate-300">Enter the 6-digit OTP sent to <span className="font-mono text-gold">{form.email}</span></p>
                  {session?.dev_mode && <p className="text-[11px] font-mono text-amber-400 mt-2" data-testid="dev-email-otp">Demo mode — email OTP: {session.dev_email_otp}</p>}
                  <div className="mt-5"><OtpBoxes value={emailOtp} onChange={setEmailOtp} color="focus:border-violet-400" testid="email-otp" /></div>
                  <p className="text-center text-xs text-slate-500 mt-4">OTP expires in <span className="font-mono text-slate-300">00:{String(seconds).padStart(2, "0")}</span></p>
                  {error && <p className="text-sm text-red-400 mt-3" data-testid="otp-error">{error}</p>}
                  <button onClick={() => verify("email", emailOtp)} disabled={loading || emailOtp.length < 6} data-testid="verify-email-otp-btn"
                    className="w-full mt-5 bg-gradient-to-r from-violet-500 to-techblue text-white font-bold text-sm py-3 rounded-sm disabled:opacity-50 hover:opacity-90 transition-opacity">
                    {loading ? "VERIFYING…" : "VERIFY EMAIL OTP"}
                  </button>
                </>
              ) : (
                <>
                  <p className="flex items-center gap-2 text-xs text-emerald-400 mb-4"><CheckCircle2 size={14} /> Email verified</p>
                  <p className="text-sm text-slate-300">Enter the 6-digit OTP sent to <span className="font-mono text-gold">{session?.phone_masked}</span></p>
                  {session?.dev_mode && <p className="text-[11px] font-mono text-amber-400 mt-2" data-testid="dev-mobile-otp">Demo mode — mobile OTP: {session.dev_mobile_otp}</p>}
                  <div className="mt-5"><OtpBoxes value={mobileOtp} onChange={setMobileOtp} color="focus:border-emerald-400" testid="mobile-otp" /></div>
                  <p className="text-center text-xs text-slate-500 mt-4">OTP expires in <span className="font-mono text-slate-300">00:{String(seconds).padStart(2, "0")}</span></p>
                  {error && <p className="text-sm text-red-400 mt-3" data-testid="otp-error">{error}</p>}
                  <button onClick={() => verify("mobile", mobileOtp)} disabled={loading || mobileOtp.length < 6} data-testid="verify-mobile-otp-btn"
                    className="w-full mt-5 bg-emerald-500 text-navy-950 font-bold text-sm py-3 rounded-sm disabled:opacity-50 hover:bg-emerald-400 transition-colors">
                    {loading ? "ACTIVATING…" : "VERIFY MOBILE OTP & ACTIVATE"}
                  </button>
                </>
              )}
              <button onClick={init} data-testid="resend-otp-btn" className="w-full text-center text-xs text-slate-500 hover:text-gold mt-4 flex items-center justify-center gap-1.5">
                <RefreshCw size={11} /> Didn't receive OTP? Resend
              </button>
            </div>
          )}
        </div>
      </section>

      {/* SCAN CARD OPTION */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 pb-14" data-testid="scan-cta-section">
        <div className="card-surface hairline-gold p-7 flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
          <span className="w-14 h-14 rounded-full border border-gold/50 bg-gold/10 flex items-center justify-center shrink-0"><ScanLine size={24} className="text-gold" /></span>
          <div className="flex-1">
            <p className="font-display font-bold text-slate-100">Have a visiting card? Skip the typing.</p>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">Scan your visiting card and we'll auto-fill the registration for you — review, submit, done in seconds.</p>
          </div>
          <Link to="/scan" data-testid="scan-register-btn" className="btn-gold shrink-0">SCAN CARD & REGISTER <ArrowRight size={15} /></Link>
        </div>
      </section>

      {/* TRUST STRIPS */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14" data-testid="secure-trust-strip">
        <div className="card-surface p-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {TRUST_STRIP.map(([Icon, t, d]) => (
            <div key={t} className="flex items-start gap-3">
              <Icon size={22} className="text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
              <div><p className="text-sm font-semibold text-slate-200">{t}</p><p className="text-[11px] text-slate-500 mt-1 leading-snug">{d}</p></div>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-8 mt-10 pt-10 border-t border-slate-800">
          {TRUST_BOTTOM.map(([Icon, t, d]) => (
            <div key={t} className="flex items-start gap-3.5">
              <span className="w-10 h-10 rounded-full border border-gold/50 bg-gold/10 flex items-center justify-center shrink-0"><Icon size={18} className="text-gold" /></span>
              <div><p className="text-sm font-bold text-techblue-light tracking-wide">{t}</p><p className="text-xs text-slate-500 mt-1 leading-snug">{d}</p></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
